import { createEffect, createSignal, For, Show, type Component, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';

import type { Attributes, InventoryItem } from '../net/protocol';
import {
  affixLine,
  affixSign,
  arrow,
  compareItems,
  dpsDelta,
  percentOf,
  requirementText,
  shortfallText,
  unmetFor,
} from './compare';
import { placeTooltip, type Point } from './tooltip';

/**
 * A floating tooltip anchored to whatever element it is placed inside.
 *
 * Extracted from the paper doll, which had it inline, because the bag needs the
 * same thing: an item you cannot wear still has stats worth reading, and
 * `title="Cannot be worn"` is not a substitute for them.
 *
 * Always a `<Portal>` with `position: fixed`. The panels around it scroll, and
 * `overflow-y: auto` forces the computed `overflow-x` to `auto` too — so any
 * tooltip positioned inside one gets clipped on both axes. That is what cut the
 * doll's left-column tooltips in half.
 */
export const FloatingTooltip: Component<{
  /** The element the tooltip points at. */
  anchor: HTMLElement | undefined;
  /** Whether it should be on screen. */
  when: boolean;
  class?: string;
  testId?: string;
  children: JSX.Element;
}> = (props) => {
  let tipEl: HTMLDivElement | undefined;
  const [at, setAt] = createSignal<Point | null>(null);

  createEffect(() => {
    if (!props.when || !props.anchor || !tipEl) {
      setAt(null);
      return;
    }
    const a = props.anchor.getBoundingClientRect();
    const t = tipEl.getBoundingClientRect();
    setAt(
      placeTooltip(
        { left: a.left, top: a.top, width: a.width, height: a.height },
        { width: t.width, height: t.height },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  });

  return (
    <Show when={props.when}>
      <Portal>
        <div
          ref={tipEl}
          class={props.class ?? 'doll__tip'}
          data-testid={props.testId}
          role="tooltip"
          style={{
            left: `${at()?.left ?? 0}px`,
            top: `${at()?.top ?? 0}px`,
            // Hidden until measured, so it never flashes in the wrong place on the
            // frame before its own size is known.
            visibility: at() ? 'visible' : 'hidden',
          }}
        >
          {props.children}
        </div>
      </Portal>
    </Show>
  );
};

/**
 * The body of an item tooltip: name, rarity, requirements, affixes, power.
 *
 * Shared by the doll and the bag so the same item reads identically wherever it
 * is. `attributes` is the player's totals, used only to mark a requirement as
 * unmet — pass null and nothing is marked.
 */
export const ItemTooltipBody: Component<{
  item: InventoryItem;
  attributes: Attributes | null;
  /** What occupies the slot this would go into, for the comparison. */
  worn?: InventoryItem;
  /** Extra line above the meta row — the two-hander note, for instance. */
  note?: string;
  /**
   * The character's current effective health, so a toughness delta can be shown as a percentage.
   *
   * Optional: without it the badge prints its absolute alone, which is still true. An item has no
   * toughness of its own — only an effect on the total — so the baseline has to come from outside.
   */
  toughness?: number;
}> = (props) => {
  const unmet = () => unmetFor(props.item.requires, props.attributes);
  const wearable = () => unmet().length === 0;
  const deltas = () => (props.worn ? compareItems(props.item, props.worn) : []);
  const dps = () => dpsDelta(props.item, props.worn);
  const toughness = () => props.item.toughnessDelta ?? 0;

  // Rounded-to-display thresholds: below these a badge would print `+0.0`, which is worse than
  // absent because it looks like a measured result.
  const showDps = () => dps() !== null && Math.abs(dps()!) >= 0.05;
  const showToughness = () => Math.abs(toughness()) >= 0.5;

  const dpsPct = () => percentOf(dps() ?? 0, props.worn?.dps ?? 0);
  /**
   * Toughness as a percentage of what the character has **now**.
   *
   * The baseline is the character's whole effective health, not the worn item's contribution — an
   * item does not have a toughness of its own, only an effect on the total. That total is on the
   * `CHARACTER` frame, so the caller passes it; without it the badge shows its absolute alone.
   */
  const toughnessPct = () => percentOf(toughness(), props.toughness ?? 0);

  const metricClass = (v: number) => ({
    'doll__tip-metric--up': v > 0,
    'doll__tip-metric--down': v < 0,
  });

  /** `+18.2%` / `-410`, with the sign always printed so colour is never the only signal. */
  const signed = (v: number | null, suffix: string, digits: number) =>
    v === null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(digits)}${suffix}`;

  return (
    <>
      <p class={`doll__tip-name doll__tip-name--${props.item.rarity.toLowerCase()}`}>
        {props.item.name}
      </p>
      <Show when={props.note}>{(note) => <p class="doll__tip-note">{note()}</p>}</Show>
      <p class="doll__tip-meta">
        {props.item.rarity} · {props.item.slot} · iLvl {props.item.itemLevel}
        <Show when={(props.item.armour ?? 0) > 0}> · {props.item.armour} armour</Show>
      </p>

      {/*
        **A weapon's DPS, as the headline it deserves.** The number a player is actually choosing
        between, and until now the bag could not show it at all — a weapon's damage band and rate are
        catalogue data the client never received.

        Every figure here is computed server-side by `World.WeaponNumbers`, from the same
        `character.AttacksPerSecond` the simulation swings on. The client formats and does not
        calculate: this arithmetic has produced two silent balance bugs already (`attack_speed`
        truncated to an integer; the band double-counted), and a second implementation here would be
        the copy no balance test measures.

        `dps` is absent on anything that is not a weapon, so a ring renders none of this.
      */}
      <Show when={(props.item.dps ?? 0) > 0}>
        <p class="doll__tip-dps" data-testid="tip-dps">
          <strong>{(props.item.dps ?? 0).toFixed(1)} DPS</strong>
          {/*
            The band and the rate are a **separate** `Show`, not assumed present because `dps` is.
            The server always sends all four together, and asserting that here with `!` was a lie to
            the type system that threw at runtime the moment anything constructed an item with a DPS
            and no band. A tooltip is the last place that should be able to crash a panel: this is
            what a player hovers over a hundred times a run.
          */}
          <Show when={(props.item.attacksPerSec ?? 0) > 0}>
            <span class="doll__tip-dps-detail">
              {(props.item.damageMin ?? 0).toFixed(0)}–{(props.item.damageMax ?? 0).toFixed(0)} damage
              · {(props.item.attacksPerSec ?? 0).toFixed(2)}/sec
            </span>
          </Show>
        </p>
      </Show>

      {/* Requirements, and the verdict. A player should never have to work out
          whether they can wear something by comparing two numbers themselves. */}
      <Show when={requirementText(props.item.requires) !== ''}>
        <p
          class="doll__tip-req"
          classList={{ 'doll__tip-req--unmet': !wearable() }}
          data-testid="tip-req"
        >
          Requires {requirementText(props.item.requires)}
        </p>
      </Show>
      <Show when={props.attributes}>
        <p
          class="doll__tip-verdict"
          classList={{ 'doll__tip-verdict--no': !wearable() }}
          data-testid="tip-verdict"
        >
          {wearable() ? 'You can equip this' : `Cannot equip — needs ${shortfallText(unmet())}`}
        </p>
      </Show>

      {/*
        **The comparison, in the two numbers that decide it.** Shown only when something is worn in
        the slot, because a delta against nothing is just the item's own stats over again.

        Sign, arrow *and* colour — never colour alone. That is the existing rule in
        `ART-DIRECTION-UI-SYSTEM.md`, and the reason `--gain` and `--loss` are held 20 ΔE apart: they
        are close in luminance by design, which is exactly what a contrast ratio cannot police.

        DPS is subtracted client-side from two numbers already on the wire; toughness arrives as a
        delta because it cannot be subtracted — it needs the whole loadout re-derived with the item in
        its socket, which only the server can do.
      */}
      {/*
        **Zero deltas are not rendered, and neither is an empty block.** A metric that does not move
        says nothing, and `0.0%` beside a real change is noise competing with it — so each badge has
        its own threshold and the container disappears when neither survives, collapsing the space
        rather than leaving a gap.

        The thresholds are display-precision rather than arbitrary: DPS prints to one decimal, so
        anything under 0.05 rounds to `+0.0` and is a lie of a different kind; toughness prints whole,
        so 0.5 is the same rule one digit over.

        **Both a percentage and an absolute**, because neither alone is actionable: `+42 DPS` is
        enormous on a dagger and marginal on a greatsword, and `+18%` does not say whether it is worth
        a slot. The percentage is omitted where there is no baseline — an empty hand has no `+∞%`.
      */}
      <Show when={props.worn && (showDps() || showToughness())}>
        <div class="doll__tip-metrics" data-testid="tip-metrics">
          <Show when={showDps()}>
            <p class="doll__tip-metric" classList={metricClass(dps() ?? 0)}>
              {arrow((dps() ?? 0) > 0 ? 1 : -1)} {signed(dpsPct(), '%', 1)}
              <span class="doll__tip-metric-abs"> DPS ({signed(dps(), '', 1)})</span>
            </p>
          </Show>
          <Show when={showToughness()}>
            <p class="doll__tip-metric" classList={metricClass(toughness())}>
              {arrow(toughness() > 0 ? 1 : -1)} {signed(toughnessPct(), '%', 1)}
              <span class="doll__tip-metric-abs">
                {' '}
                Toughness ({signed(toughness(), '', 0)})
              </span>
            </p>
          </Show>
        </div>
      </Show>

      {/* Green for a buff, red for a debuff — colour rather than the sign alone, because
          a leading `−` is invisible in a tooltip with eight lines on it. Both tokens are
          held to contrast in `styles/palette.test.ts`. */}
      <For each={props.item.affixes}>
        {(affix) => (
          <p
            class="doll__tip-affix"
            classList={{
              'doll__tip-affix--buff': affixSign(affix) > 0,
              'doll__tip-affix--debuff': affixSign(affix) < 0,
            }}
            data-testid={`tip-affix-${affix.stat}`}
          >
            {affixLine(affix)}
          </p>
        )}
      </For>
      <Show when={props.item.power}>
        {(power) => (
          <p class="doll__tip-power">
            {power()}
            {/* The mechanic is not simulated; saying so beats implying it works. */}
            <span class="doll__tip-inert"> (not yet active)</span>
          </p>
        )}
      </Show>

      {/* The comparison. Not "is this better" — the simulation does not read most
          of these stats yet, so a verdict would be a confident lie — but "here is
          what changes", which the player can judge. */}
      <Show when={props.worn}>
        {(worn) => (
          <div class="doll__tip-compare" data-testid="tip-compare">
            <hr class="rule" />
            <p class="doll__tip-compare-head">Against {worn().name} (iLvl {worn().itemLevel})</p>
            <For each={deltas().filter((d) => d.direction !== 0)}>
              {(d) => (
                <p
                  class="doll__tip-delta"
                  classList={{
                    'doll__tip-delta--up': d.direction > 0,
                    'doll__tip-delta--down': d.direction < 0,
                  }}
                >
                  <span class="doll__tip-arrow">{arrow(d.direction)}</span> {d.label} {d.from} →{' '}
                  {d.to}
                </p>
              )}
            </For>
            <Show when={deltas().every((d) => d.direction === 0)}>
              <p class="doll__tip-delta">Identical stats.</p>
            </Show>
          </div>
        )}
      </Show>
    </>
  );
};
